
import { GLOBAL_SEARCH_URL } from "@/utils/constants/constants.js"; // make sure it’s exported
import axios from "axios";

export default {
  name: "GlobalSearch",
  props: {
    show: { type: Boolean, default: false },
    searchQuery: { type: String, default: "" },
    project_id: { type: [Number, String], default: null },
    headers: { type: Object, default: () => ({}) },
  },
  data: () => ({
    is_searching: false,
    error_message: "",
    // UI filters (client-side for now)
    selected_status: "All",
    selected_date_range: "Last 30 days",
    // backend results
    results: [],      // array of normalized items from backend
    total_count: 0,   // backend count
    // request control
    _debounce_id: null,
    _cancel_source: null,
    // type filter UI (maps to backend search_types)
    selected_types: new Set(), // e.g. new Set(['application', 'user'])
  }),
  computed: {
    has_filters() {
      return (
        this.selected_status !== "All" ||
        this.selected_date_range !== "Last 30 days" ||
        this.selected_types.size > 0
      );
    },
    filtered_results() {
      // For now, only application items get status/date filtering client-side.
      const by_type = (r) =>
        this.selected_types.size === 0 || this.selected_types.has(r.source);

      const by_status = (r) => {
        if (r.source !== "application") return true;
        if (this.selected_status === "All") return true;
        const status_label = r.data?.status_label || r.badges?.[1] || "";
        return status_label === this.selected_status;
      };

      const by_date = (r) => {
        if (r.source !== "application") return true;
        const submitted =
          r.data?.submitted_date || r.data?.date_created || r.data?.created_at;
        if (!submitted) return true;

        const date = new Date(submitted);
        const now = new Date();
        const diffDays = (now - date) / (1000 * 60 * 60 * 24);

        const range = this.selected_date_range;
        if (range === "Last 7 days") return diffDays <= 7;
        if (range === "Last 30 days") return diffDays <= 30;
        if (range === "Last 90 days") return diffDays <= 90;
        return true;
      };

      return this.results.filter((r) => by_type(r) && by_status(r) && by_date(r));
    },
  },
  methods: {
    handle_search(query) {
      // debounce to reduce traffic
      clearTimeout(this._debounce_id);
      this._debounce_id = setTimeout(() => this._do_search(query), 250);
    },


    async _do_search(query_text) {
      this.error_message = "";

      const trimmed_query = (query_text || "").trim();
      if (!trimmed_query) {
        this.results = [];
        this.total_count = 0;
        this.is_searching = false;
        return;
      }

      // Cancel previous request if any
      if (this._cancel_source) {
        this._cancel_source.cancel("New search started");
      }
      this._cancel_source = axios.CancelToken.source();

      this.is_searching = true;

      try {
        // Query params sent to the API
        const query_params = {
          search_query: trimmed_query, // if your backend expects `q`, rename accordingly
          limit: 50,
        };
        if (this.project_id) {
          query_params.project_id = this.project_id;
        }

        // Optional: include search types if you support them
        const selected_search_types = Array.from(this.selected_types || []);

        // Axios config (headers, params, serializer, cancel token)
        const request_config = {
          headers: this.headers,
          params: query_params,
          cancelToken: this._cancel_source.token,
          paramsSerializer: (params_obj) => {
            const url_params = new URLSearchParams();

            // Append scalar params
            Object.entries(params_obj).forEach(([param_key, param_value]) => {
              if (param_key === "search_types") return; // handled below
              if (param_value !== undefined && param_value !== null && param_value !== "") {
                url_params.append(param_key, param_value);
              }
            });

            // Append repeated `search_types`
            if (Array.isArray(params_obj.search_types)) {
              params_obj.search_types.forEach((type_value) => {
                url_params.append("search_types", type_value);
              });
            }

            // Also append selected_search_types from component state (if any)
            selected_search_types.forEach((type_value) => {
              url_params.append("search_types", type_value);
            });

            return url_params.toString();
          },
        };

        const { data } = await axios.get(GLOBAL_SEARCH_URL, request_config);

        if (data?.status !== "success") {
          this.error_message = data?.message || "Search failed.";
          this.results = [];
          this.total_count = 0;
        } else {
          this.results = Array.isArray(data.results) ? data.results : [];
          this.total_count = Number(data.count || 0);
        }
      } catch (error_obj) {
        if (!axios.isCancel(error_obj)) {
          this.error_message =
            error_obj?.response?.data?.message || error_obj?.message || "Network error.";
          this.results = [];
          this.total_count = 0;
        }
      } finally {
        this.is_searching = false;
      }
    },


    // UI filters
    select_status(status) {
      this.selected_status = status;
    },
    select_date_range(range) {
      this.selected_date_range = range;
    },
    toggle_type(typeKey) {
      if (this.selected_types.has(typeKey)) this.selected_types.delete(typeKey);
      else this.selected_types.add(typeKey);
      // requery backend when type filter changes
      this.handle_search(this.searchQuery);
    },
    clear_filters() {
      this.selected_status = "All";
      this.selected_date_range = "Last 30 days";
      this.selected_types = new Set();
      this.handle_search(this.searchQuery);
    },

    // Safer HTML escaping using a Map for guaranteed property access
    escape_html(str) {
      const escapeMap = new Map([
        ['&', '&amp;'],
        ['<', '&lt;'],
        ['>', '&gt;'],
        ['"', '&quot;'],
        ["'", '&#39;'],
        ['`', '&#96;'],
        ['=', '&#61;'],
        ['/', '&#47;']
      ]);

      return String(str).replace(/[&<>"'`=/]/g, (char) => {
        return escapeMap.get(char) || char;
      });
    },

    highlight(text) {
      const raw_text = text ?? "";
      const raw_query = this.searchQuery ?? "";

      const safe_text = this.escape_html(raw_text);
      if (!raw_query.trim()) return safe_text;

      try {
        const safe_query = this.escape_html(raw_query);

        // Limit the length to prevent ReDoS
        if (safe_query.length > 50) {
          return safe_text;
        }

        // Manual case-insensitive search without RegExp
        const lower_text = safe_text.toLowerCase();
        const lower_query = safe_query.toLowerCase();
        const parts = [];
        let last_index = 0;
        let index = lower_text.indexOf(lower_query, last_index);

        // If no match found, return original text
        if (index === -1) {
          return safe_text;
        }

        // Split text manually based on matches
        while (index !== -1) {
          // Add text before match
          if (index > last_index) {
            parts.push({
              text: safe_text.substring(last_index, index),
              highlight: false
            });
          }
          // Add highlighted match (preserve original case)
          parts.push({
            text: safe_text.substring(index, index + safe_query.length),
            highlight: true
          });
          last_index = index + safe_query.length;
          index = lower_text.indexOf(lower_query, last_index);
        }

        // Add remaining text
        if (last_index < safe_text.length) {
          parts.push({
            text: safe_text.substring(last_index),
            highlight: false
          });
        }

        return parts;

      } catch {
        return safe_text;
      }
    },

    on_keydown(e) {
      if (e.key === "Escape") this.$emit("close");
    },

    extract_status(item) {
      if (!item) return "";
      const from_data  = item.data?.status_label || item.data?.status;
      const from_extra = item.extra?.status_label || item.extra?.status;
      const from_badge = Array.isArray(item.badges)
        ? item.badges.find(b => /pending|approved|rejected|in.?progress|under.?review/i.test(b))
        : null;
      const raw = from_data || from_extra || from_badge || "";
      return typeof raw === "string" ? raw.trim() : "";
    },

      // "In Progress" / "in-progress" / "In_Progress" -> "in-progress"
    to_kebab_status(s) {
      if (!s) return "";
      return s
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .replace(/\s/g, "-");
    },

    // "In Progress" / "in-progress" / "In_Progress" -> "in_progress"  (DB format)
    to_snake_status(s) {
      if (!s) return "";
      return s
        .replace(/[-\s]+/g, "_")     // spaces/dashes -> underscores
        .replace(/_+/g, "_")         // collapse duplicates
        .trim()
        .toLowerCase();
    },

    on_select(item) {
      const source = item?.source;
      if (!source) return;

      if (source === "user") {
        this.$router.push("/user_management");
        return;
      }

      if (source === "entity_type") {
        const raw_status = this.extract_status(item);
        const query = { entity_type_id: String(item.source_id) };
        if (raw_status) {
          query.status = this.to_kebab_status(raw_status);
          query.status_db = this.to_snake_status(raw_status);
        }
        this.$router.push({ path: "/pending_applications", query });
        return;
      }

      if (source === "entity_profile" || source === "application") {
        // prefer parent app/status if this is a sub-entity
        const is_sub = !!item.extra?.is_subentity;

        const raw_status = is_sub
          ? (item.extra?.parent_status ?? item.extra?.status ?? this.extract_status(item))
          : (item.extra?.status ?? this.extract_status(item));

        const status_snake = this.to_snake_status(raw_status);

        const application_id = is_sub
          ? (item.extra?.parent_application_id ?? item.extra?.application_id ?? (source === "application" ? item.source_id : ""))
          : (item.extra?.application_id ?? (source === "application" ? item.source_id : ""));

        // entity_profile_id in the URL remains the clicked entity (child if sub-entity)
        const entity_profile_id =
          source === "entity_profile"
            ? item.source_id
            : (item.extra?.entity_id ?? item.data?.entity_profile_id ?? item.data?.entity_id ?? "");

        const query = {
          application_id: application_id ? String(application_id) : "",
          entity_profile_id: entity_profile_id ? String(entity_profile_id) : "",
        };
        if (status_snake) query.status = status_snake;

        this.$router.push({ path: "/overview_of_profile", query });
        return;
      }

    },



  },
  watch: {
    // FIX: your old code watched 'search_query' which doesn't exist
    searchQuery(newVal) {
      this.handle_search(newVal);
    },
    show(val) {
      if (val) window.addEventListener("keydown", this.on_keydown);
      else window.removeEventListener("keydown", this.on_keydown);
    },
  },
  mounted() {
    if (this.show) window.addEventListener("keydown", this.on_keydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.on_keydown);
    clearTimeout(this._debounce_id);
    if (this._cancel_source) this._cancel_source.cancel("Component unmounted");
  },
};
